import { KeyboardArrowDown } from "@mui/icons-material";
import {
    Box,
    Button,
    Card,
    FormControl,
    FormLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    styled,
} from "@mui/material";
import clsx from "clsx";
import { ReactNode, UIEvent, createRef, useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { ITEMS_PER_PAGE_ALL } from "../../pages/data-browser/selectors/filters-and-pagination";
import {
    IWithClassName,
    IWithThemeProps,
    scrollStyle,
} from "../../theme/default.theme";
import { HeaderFormatEnum } from "./enum/header-format";
import TableArrow from "./table-arrow";

export interface ITableColumn {
    title: string;
    id: string;
    sortable: boolean;
    width?: string | number;
    icon?: string;
}

interface IOwnProps {
    columns: Array<ITableColumn>;
    rowsPerPage: number;
    totalRows: number;
    currentPage: number;
    onRowsPerPageChange?: (rows: number) => void;
    onPageChange?: (page: number) => void;
    onColumnOrder?: (
        columnIndex: number,
        column: ITableColumn,
        direction: "asc" | "desc",
    ) => void;
    renderCell: (
        rowIndex: number,
        columnIndex: number,
        column: ITableColumn,
    ) => ReactNode;
    renderNoDataMessage?: () => ReactNode;
    customHeader?: ReactNode;
    headerFormat?: HeaderFormatEnum;
    uniqueRowId: (rowIndex: number) => string;
    isLoading?: boolean;
}

const defaultColumnWidth = 200;

const TableComponent = ({
    className,
    columns,
    rowsPerPage,
    totalRows,
    currentPage,
    onRowsPerPageChange,
    onPageChange,
    onColumnOrder,
    renderCell,
    renderNoDataMessage,
    customHeader,
    headerFormat,
    uniqueRowId,
    isLoading,
}: IWithClassName & IOwnProps) => {
    const tableHeaderContainerRef = createRef<HTMLDivElement>();
    const [sortDirection, setSortDirection] = useState<"asc" | "desc" | null>(
        null,
    );
    const [sortedColumnIndex, setSortedColumnIndex] = useState<number | null>(
        null,
    );
    const totalPages =
        rowsPerPage === ITEMS_PER_PAGE_ALL
            ? 1
            : Math.ceil(totalRows / rowsPerPage);
    const baseRowCalculation = (currentPage - 1) * rowsPerPage;
    const rowsLeft = totalRows - baseRowCalculation;
    const rowsToDisplay =
        rowsPerPage === ITEMS_PER_PAGE_ALL
            ? totalRows
            : Math.min(rowsPerPage, rowsLeft);

    const handleColumnSort = (columnIndex: number, column: ITableColumn) => {
        const newDirection = sortDirection === "asc" ? "desc" : "asc";
        setSortDirection(newDirection);
        setSortedColumnIndex(columnIndex);

        onColumnOrder?.(columnIndex, column, newDirection);
    };

    const loadingRows = useMemo(
        () =>
            totalRows > 0 && rowsPerPage > totalRows ? totalRows : rowsPerPage,
        [rowsPerPage, totalRows],
    );

    const handleRowsPerPageChange = (
        event: SelectChangeEvent<number | "All">,
    ) => {
        const newValue = event.target.value;
        if (newValue === "All") {
            onRowsPerPageChange?.(ITEMS_PER_PAGE_ALL);
        } else {
            onRowsPerPageChange?.(newValue as number);
        }
    };

    const renderLoading = () =>
        Array.from({
            length: rowsPerPage === ITEMS_PER_PAGE_ALL ? 50 : loadingRows,
        }).map((_, rowIndex) => (
            <TableRow key={uniqueRowId(baseRowCalculation + rowIndex)}>
                {columns.map((column) => (
                    <TableCell
                        key={column.id}
                        className={`columnWidth-${column.id}`}
                    >
                        <Skeleton />
                    </TableCell>
                ))}
            </TableRow>
        ));

    const rowScrollHandler = (event: UIEvent<HTMLElement>) =>
        tableHeaderContainerRef.current?.scrollTo({
            left: event.currentTarget.scrollLeft,
        });

    const renderRows = () =>
        Array.from({ length: rowsToDisplay }).map((_, rowIndex) => (
            <TableRow key={uniqueRowId(baseRowCalculation + rowIndex)}>
                {columns.map((column, columnIndex) => (
                    <TableCell
                        key={column.id}
                        className={`columnWidth-${column.id}`}
                    >
                        <Typography variant="body2" className="cell-content">
                            {renderCell(
                                baseRowCalculation + rowIndex,
                                columnIndex,
                                column,
                            )}
                        </Typography>
                    </TableCell>
                ))}
            </TableRow>
        ));

    const renderNoData = () => {
        if (!renderNoDataMessage) {
            return null;
        }

        return (
            <TableRow>
                <TableCell colSpan={columns.length}>
                    <Typography variant="body2" className="no-cell-content">
                        {renderNoDataMessage()}
                    </Typography>
                </TableCell>
            </TableRow>
        );
    };

    const displayRangeStart = totalRows ? baseRowCalculation + 1 : 0;
    const displayRangeEnd =
        rowsPerPage === ITEMS_PER_PAGE_ALL
            ? totalRows
            : Math.min(baseRowCalculation + rowsPerPage, totalRows);

    return (
        <div className={clsx("table-container", className)}>
            <Card className="header-card">
                <Box className="custom-header-container">{customHeader}</Box>
                <Box
                    className={clsx(
                        "rows-per-page-container",
                        headerFormat === HeaderFormatEnum.PageSizeLeft &&
                            "order-first",
                    )}
                >
                    <FormControl variant="outlined">
                        <FormLabel component="legend">
                            Results per page
                        </FormLabel>
                        <Select
                            label="Results per page"
                            onChange={handleRowsPerPageChange}
                            value={
                                rowsPerPage === ITEMS_PER_PAGE_ALL
                                    ? "All"
                                    : rowsPerPage
                            }
                            IconComponent={KeyboardArrowDown}
                        >
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                            <MenuItem value={200}>200</MenuItem>
                            <MenuItem value="All">All</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Card>
            <Box className="table-cont">
                <Box className="sticky" ref={tableHeaderContainerRef}>
                    <Table>
                        <TableHead id="header-row">
                            <TableRow id="header-row-child">
                                {columns.map((column, columnIndex) => (
                                    <TableCell
                                        key={column.id}
                                        onClick={() =>
                                            column.sortable &&
                                            handleColumnSort(
                                                columnIndex,
                                                column,
                                            )
                                        }
                                        className={clsx(
                                            column.sortable ? "sortable" : "",
                                            `columnWidth-${column.id}`,
                                        )}
                                    >
                                        <Box className="header-cell">
                                            {column.icon && (
                                                <img
                                                    src={column.icon}
                                                    alt={column.title}
                                                    className="header-cell-icon"
                                                />
                                            )}
                                            <Typography>
                                                {column.title}
                                            </Typography>
                                            {column.sortable && (
                                                <TableArrow
                                                    isSort={
                                                        columnIndex ===
                                                        sortedColumnIndex
                                                    }
                                                    sortDirection={
                                                        sortDirection
                                                    }
                                                />
                                            )}
                                        </Box>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                    </Table>
                </Box>
                <Box className="scroll" onScroll={rowScrollHandler}>
                    <Table>
                        <TableBody>
                            {isLoading && renderLoading()}
                            {!isLoading && totalRows > 0 && renderRows()}
                            {!isLoading && totalRows === 0 && renderNoData()}
                        </TableBody>
                    </Table>
                </Box>
            </Box>
            <Box className="pagination-container">
                <div className="pagination">
                    <Button
                        onClick={() => onPageChange?.(currentPage - 1)}
                        disabled={currentPage <= 1}
                        aria-label="Previous page"
                    >
                        <img src="/assets/left.svg" alt="Previous" />
                    </Button>
                    <span>
                        {displayRangeStart}-{displayRangeEnd} of {totalRows}
                    </span>
                    <Button
                        onClick={() => onPageChange?.(currentPage + 1)}
                        disabled={currentPage >= totalPages}
                        aria-label="Next page"
                    >
                        <img src="/assets/right.svg" alt="Next" />
                    </Button>
                </div>
            </Box>
        </div>
    );
};

export default styled(TableComponent)(
    ({ theme, columns }: IWithThemeProps & IOwnProps) => ({
        ".sticky": {
            position: "sticky",
            top: 45,
            zIndex: 100,
            overflowX: "hidden",
        },
        ".header-card": {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderRadius: "20px 20px 0 0",
            padding: theme?.spacing(0, 3),
        },
        ".custom-header-container": {
            display: "flex",
            alignItems: "center",
        },
        ".cell-content": {
            color: theme?.palette.listsTable.textColor,
            fontSize: 16,
        },
        ".no-cell-content": {
            color: theme?.palette.listsTable.textColor,
            fontSize: 18,
            fontWeight: 700,
            textAlign: "center",
        },
        ".order-first": {
            order: -1,
        },
        ".rows-per-page-container": {
            display: "flex",
            alignItems: "center",
            "& .MuiFormControl-root": {
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
            },
            "& .MuiFormLabel-root": {
                display: "flex",
                marginRight: theme?.spacing(1),
                "&.Mui-focused": {
                    color: theme?.palette.listsTable.selectTextColor,
                },
            },
            "& .MuiOutlinedInput-root": {
                "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "transparent",
                },
            },
        },
        ".header-cell": {
            display: "flex",
            alignItems: "center",
            ".MuiTypography-root": {
                whiteSpace: "nowrap",
            },
        },
        ".header-cell.compact-column": {
            display: "flex",
            flexDirection: "column",
        },
        ".sortable": {
            cursor: "pointer",
        },
        ".MuiSelect-icon": {
            color: theme?.palette.listsTable.dropdownColor,
        },
        ".MuiInputBase-root": {
            color: theme?.palette.listsTable.selectTextColor,
        },
        ".MuiTableCell-root:first-of-type": {
            overflowX: "hidden",
        },
        ...columns.reduce(
            (acc, column) => ({
                ...acc,
                [`.columnWidth-${column.id}`]: {
                    width: column.width ? column.width : defaultColumnWidth,
                    minWidth: column.width,
                    maxWidth: column.width,
                    overflowWrap: "break-word",
                },
            }),
            {},
        ),
        ".pagination": {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            color: theme?.palette.listsTable.pagination,
        },
        ".pagination-container": {
            borderRadius: "0 0 20px 20px",
            backgroundColor: theme?.palette.collapsibleFeatures.bgColor,
            minHeight: 56,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
        },
        ".table-cont": {
            overflow: "visibale",
        },
        ".scroll": {
            overflow: "auto",
            ...scrollStyle(theme?.spacing(2) ?? "", true),
        },
    }),
);
